import {Injectable} from '@angular/core';

import {BehaviorSubject, combineLatest, from, Observable, of} from "rxjs";
import {catchError, map, tap} from "rxjs/operators";

import {ApiService} from "@services/api.service";
import {UserService} from "@services/user/user.service";
import {StorageService} from "@services/storage/storage.service";

import {appRoutingEnum} from "@enums/appRoutingEnum";
import {StatusEnum} from "@enums/statusEnum";

import {StandardResponse} from "@interfaces/general.types";
import {HttpService} from "@services/http/http.service";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _isLogin: BehaviorSubject<boolean>;
  public readonly isLogin$: Observable<boolean>;

  private _isPaying: BehaviorSubject<boolean>;
  public readonly isPaying$: Observable<boolean>;

  private _isAccountLessThan2DaysOld: BehaviorSubject<boolean>;
  public readonly isAccountLessThan2DaysOld$: Observable<boolean>;

  private _status: BehaviorSubject<StatusEnum>;
  public readonly status$: Observable<StatusEnum>;

  public readonly isInitialized = false;

  constructor(
    private api: ApiService,
    private http: HttpService,
    private storage: StorageService,
    private userProfile: UserService
  ) {
    this._isLogin = new BehaviorSubject<boolean>(false);
    this.isLogin$ = this._isLogin.asObservable();

    this._isPaying = new BehaviorSubject<boolean>(false);
    this.isPaying$ = this._isPaying.asObservable();

    this._isAccountLessThan2DaysOld = new BehaviorSubject<boolean>(false);
    this.isAccountLessThan2DaysOld$ = this._isAccountLessThan2DaysOld.asObservable();

    this._status = new BehaviorSubject<StatusEnum>(StatusEnum.LOGGED_OUT);
    this.status$ = this._status.asObservable();
    combineLatest([this.userProfile.getUsername(), this.userProfile.getPassword()])
      .subscribe(([username, password]) => this.formSubmit(username, password).subscribe())

    // this._status.subscribe(val => console.log(`status: ${val}`))
    // this._isLogin.subscribe(val => console.log(`isLogin: ${val}`))
    // this._isPaying.subscribe(val => console.log(`is paying: ${val}`))
    // this._isAccountLessThan2DaysOld.subscribe(val => console.log(`isLessThan2DaysOld: ${val}`))

  }

  logout() {
    this.setIsLogin(false);
    this.setIsPaying(false);
    this.setIsAccountLessThan2DaysOld(false);
    this.setStatus(StatusEnum.LOGGED_OUT);
    this.userProfile.logout();
    this.storage.remove('userData').then();
  }

  /**
   * Posts a login request to the server, persists data if necessary, and returns an object with instructions for the
   * component to display.
   * @param username
   * @param password
   * @returns  An {@link LoginResponseInstructions} with info for the component to display.
   * @type {(type:string)=> LoginResponseInstructions}
   */
  formSubmit(username?: string, password?: string): Observable<LoginResponseInstructions> {
    let header: any;
    if (username && password) {
      header = this.http.setOneTimeHeader(username, password);
    }
    username = username ?? 'nologin';
    password = password ?? 'nopassword';
    this.userProfile.password = password;
    this.userProfile.username = username;
    //console.log(this.http.headerLogin);
    return this.http.post(this.http.url + '/user/login/', '', this.http.headerLogin)
      .pipe(
        tap(serverResponse => this.updateStateFromLoginInstructions(serverResponse, password)),
        map((data: any) => AuthService.mapServerResponseToInstructions(data, data.username, password)),
        catchError((err: any) => {
          console.log(err);
          if (err?.status == 403) {
            this.logout();
            return of({error: err.error});
          } else {
            return of({});
          }
        })
      );
  }

  /**
   * Persists the username and password, as well as other data received from the server on a successful login request.
   * @param serverResponse - The response from the server.
   * @param password
   * @type {(serverResponse:any, username: string, password: string)=>void}
   */
  private updateStateFromLoginInstructions(serverResponse:any, password:any): void {
    this.userProfile.username = serverResponse.username;
    this.userProfile.password = password;
    this.storage.setItem('user_id', serverResponse.id).then();
    this.storage.setItem('user_photo', serverResponse.photo).then();
    this.setIsPaying((serverResponse.gender == 0) ? true : serverResponse.userIsPaying);
    this.setStatus(serverResponse.status);
    this.setIsLogin(!!serverResponse.id);
    this.setIsAccountLessThan2DaysOld(serverResponse.is2D);
    if (serverResponse.status) {
      let data = {status: 'init', username: serverResponse.username, password};
      this.storage.setItem('fingerAuth', data).then();
      this.storage.setItem('user_photo', serverResponse.photo).then();
      this.storage.setItem('userData', serverResponse).then();
      this.api.data.current = serverResponse;
      this.storage.setItem('username', serverResponse.username).then();
      this.http.setOneTimeHeader(serverResponse.username,serverResponse.password,serverResponse.bash);
      //console.log(serverResponse);
    }
    this.storage.getItem('deviceToken').then((deviceToken) => {
      this.api.sendPhoneId(deviceToken as string);
    });
  }

  /**
   * Maps the raw server response to a setItem on instructions for the components to consume.
   * @param serverResponse - The response from the server.
   * @param username
   * @param password
   * @returns Returns a {@link LoginResponseInstructions} object.
   * @type {(serverResponse:any)=>{ destination?: appRoutingEnum, state?: {}, toastMessage?: string }}
   */
  private static mapServerResponseToInstructions(serverResponse:any, username:any, password:any): LoginResponseInstructions {
    let instruction: any = {};
    if (serverResponse.app) {
      if (serverResponse.app == StatusEnum.NOT_ACTIVATED) {
        instruction.destination = appRoutingEnum.ACTIVATION;
      } else if (serverResponse.app == StatusEnum.NO_IMG && serverResponse.gender == 0) {
        const user = {id: serverResponse.id};
        instruction.toastMessage = "לכניסה לאתר ריצ'דייט יש להעלות תמונה";
        instruction.destination = appRoutingEnum.CHANGE_PHOTO;
        instruction.state = {user, username, password};
      } else if (serverResponse.userIsPaying == 0 && serverResponse.gender == 1) {
        instruction.destination = appRoutingEnum.SUBSCRIPTION;
      } else {
        instruction.destination = appRoutingEnum.HOME;
        instruction.state = {params: StatusEnum.LOGIN, username, password};
      }
    } else {
      instruction.error = serverResponse?.error ? serverResponse.error : 'לא ניתן היה לבצע כניסה אפליקציה, אנא נסו שטב בהמשך.';
    }
    return instruction;
  }

  public setIsLogin(value: boolean) {
    if (value != this.getIsLogin()) {
      this._isLogin.next(value);
      this.storage.setItem('isLogin', value).then();
    }
  }

  public getIsLogin(): boolean {
    return this._isLogin.getValue();
  }

  public getIsLoginFromStorage(): Observable<boolean> {
    return from(this.storage.getItem('isLogin')).pipe(
      map(isLogin => isLogin?.toString() === 'true'));
  }

  public setIsPaying(value: boolean) {
    //console.log('is_paying: ' + value);

    if (value != this.getIsPaying()) {
      this._isPaying.next(value);
      this.storage.setItem('isPaying', value).then()
    }
  }

  public getIsPaying(): boolean {
    return this._isPaying.getValue();
  }

  public getIsPayingFromStorage(): Observable<boolean> {
    return from(this.storage.getItem('isPaying')).pipe(map(isPaying => isPaying?.toString() === 'true'))
  }

  public setIsAccountLessThan2DaysOld(value: boolean) {
    if (value != this.getIsAccountLessThan2DaysOld()) {
      this._isAccountLessThan2DaysOld.next(value);
    }
  }

  public getIsAccountLessThan2DaysOld(): boolean {
    return this._isAccountLessThan2DaysOld.getValue();
  }

  public setStatus(value: StatusEnum) {
    if (value != this.getStatus()) {
      this._status.next(value);
      this.storage.setItem('status', value).then()
    }
  }

  public getStatus(): StatusEnum {
    return this._status.getValue();
  }

  getStatusFromStorage(): Observable<StatusEnum> {
    return from(this.storage.getItem('status')).pipe(map(status => status?.toString() as StatusEnum));
  }
}

export interface LoginResponseInitial {
  id: string,
  is2D: number,
  isPay: boolean,
  photo: string,
  status: boolean,
  userIsPaying: boolean,
  username: string
}

export interface LoginResponseInstructions {
  destination?: appRoutingEnum,
  state?: {},
  toastMessage?: string,
  error?: string
}
